// ─────────────────────────────────────────────────────────────────
// svgSanitize.js — allowlist sanitizer for model-emitted inline SVG (Phase 3)
//
// Threat model: the diagram SVG comes from the LLM and must be treated as
// untrusted. We render it inline, so it is an XSS sink. This sanitizer parses
// the SVG, walks the tree, and keeps ONLY an allowlist of geometry/text elements
// and presentation attributes. Everything else — <script>, <foreignObject>,
// event handlers (on*), href/xlink:href, external/url() references — is removed.
//
// Fail-closed contract: any parse error, a non-<svg> root, or a tree that
// retains nothing usable returns null. The caller (DiagramBlock) renders nothing
// on null — it NEVER falls back to injecting the raw string.
// ─────────────────────────────────────────────────────────────────

// Structural + shape + text elements. No <image>, <use>, <a>, <foreignObject>,
// <script>, <style>, <animate*> — those are injection or external-fetch vectors.
const ALLOWED_TAGS = new Set([
  'svg', 'g', 'rect', 'line', 'circle', 'ellipse', 'polyline', 'polygon',
  'path', 'text', 'tspan', 'defs', 'marker',
]);

// Presentation + geometry attributes only. Deliberately excludes href,
// xlink:href, style (url()/expression risk), and any on* handler.
const ALLOWED_ATTRS = new Set([
  // svg root / viewport
  'viewbox', 'width', 'height', 'xmlns', 'preserveaspectratio',
  // geometry
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'width', 'height', 'points', 'd', 'dx', 'dy', 'transform',
  // marker plumbing (safe — internal refs only, validated below)
  'id', 'markerwidth', 'markerheight', 'refx', 'refy', 'orient',
  'markerstart', 'markerend', 'markerunits',
  // presentation
  'fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width',
  'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-opacity',
  'opacity', 'text-anchor', 'dominant-baseline', 'font-size', 'font-family',
  'font-weight', 'font-style', 'letter-spacing',
]);

// Attribute VALUE guards. A value containing any of these is dropped even when
// the attribute name is allowlisted — blocks javascript:, data:, and url()/
// expression()/external references that could smuggle script or fetch.
const DANGEROUS_VALUE = /(javascript:|expression\s*\(|url\s*\(|data:|<|&#)/i;

// marker-start / marker-end reference a local marker by url(#id). url() is in the
// dangerous-value list (correctly), so marker refs are dropped — acceptable: a
// missing arrowhead degrades the diagram, it doesn't break it. Keeping the
// attack surface small is worth more than arrowheads.

function scrubElement(el, doc) {
  // Drop disallowed elements entirely (removes their subtree too).
  const tag = el.tagName ? el.tagName.toLowerCase() : '';
  if (!ALLOWED_TAGS.has(tag)) {
    el.remove();
    return;
  }

  // Scrub attributes: remove anything not allowlisted, any on* handler, any
  // namespaced attr (xlink:*), and any allowlisted attr whose value is dangerous.
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value || '';
    const disallowed =
      name.startsWith('on') ||
      name.includes(':') ||              // xlink:href and any other namespaced attr
      !ALLOWED_ATTRS.has(name) ||
      DANGEROUS_VALUE.test(value);
    if (disallowed) el.removeAttribute(attr.name);
  }

  // Recurse over a static copy — children may be removed during the walk.
  for (const child of Array.from(el.children)) {
    scrubElement(child, doc);
  }
}

/**
 * Sanitize a raw inline-SVG string. Returns a cleaned SVG string, or null when
 * the input cannot be safely rendered (fail-closed).
 *
 * @param {string} raw - untrusted SVG markup
 * @returns {string|null}
 */
export function sanitizeSvg(raw) {
  if (!raw || typeof raw !== 'string') return null;
  // No DOMParser (non-browser / SSR) → cannot safely sanitize → render nothing.
  if (typeof DOMParser === 'undefined') return null;

  let doc;
  try {
    doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
  } catch {
    return null;
  }

  // Parser-level error (malformed XML) surfaces as a <parsererror> node.
  if (!doc || doc.getElementsByTagName('parsererror').length > 0) return null;

  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== 'svg') return null;

  scrubElement(root, doc);

  // Re-check the root survived as an <svg> and carries some drawable content.
  if (!root || root.tagName.toLowerCase() !== 'svg') return null;
  if (root.children.length === 0) return null;

  // Belt-and-braces: serialize and reject if any obviously dangerous token
  // survived (e.g. an exotic-cased handler the attribute walk missed).
  let out;
  try {
    out = new XMLSerializer().serializeToString(root);
  } catch {
    return null;
  }
  if (/<script|\son\w+\s*=|javascript:|<foreignobject/i.test(out)) return null;

  return out;
}
